export interface MapperBundle {
  id: string;
  yyyymm: string;
  format_version: string;
  path: string;
  built_at: string;
  counts: {
    msgtypes?: number;
    edi_anchors?: number;
    bo4e_targets?: number;
    entries?: number;
    manual_entries?: number;
  };
}

export interface Transform {
  type: string;
  table?: Record<string, string> | null;
  edi_qualifier?: string | null;
  target?: string | null;
  value?: string | null;
  raw?: string | null;
}

/** EDI-Anker — sowohl im Skelett (`edi_anchors`) als auch am Entry.
 * seg_name/de_name/example/value_pool sind BA-Kontext aus MIG+AHB (Skelett);
 * an Alt-Entries können sie fehlen. */
export interface EdiAnchor {
  nr?: string;
  message_type: string;
  segment_group?: string | null;
  segment: string;
  qualifier?: string | null;
  data_element?: string | null;
  component?: string | null;
  value_pool?: string[];
  seg_name?: string | null;
  de_name?: string | null;
  example?: string | null;
}

export interface MappingEntry {
  mapping_id: string;
  version: number;
  edi_anchor: EdiAnchor;
  bo4e_anchor: { bo: string; path: string };
  transform: Transform;
  direction: string;
  status: string;
  notes?: string;
}

export interface Bo4eTarget {
  bo: string;
  path: string;
  type?: string;
  cardinality?: string;
  enum?: string[];
}

/** Schema-Doku eines BO4E-Zielfeldes (/bo4e-field). */
export interface Bo4eFieldInfo {
  bo: string;
  path: string;
  found: boolean;
  type?: string | null;
  description?: string | null;
  enum?: string[] | null;
  enum_name?: string | null;
  enum_description?: string | null;
  ref?: string | null;
}

export interface Skeleton {
  format_version: string;
  message_type: string;
  edi_anchors: EdiAnchor[];
  bo4e_targets: Bo4eTarget[];
}

/** Eine EDI-Quelle eines BO-Feldes (BO-Perspektive, /bo-view). */
export interface BoSource {
  mapping_id: string;
  edi_anchor: EdiAnchor;
  transform: Transform;
  status?: string;
  message_type: string;
}

/** Ein Feld eines Geschäftsobjekts mit seinen EDI-Quellen. */
export interface BoField {
  path: string;
  type?: string | null;
  sources: BoSource[];
}

/** Antwort von /bo-view: ein BO mit Feldern + Quellen. */
export interface BoView {
  bundle: string;
  bo: string;
  message_type: string | null;
  fields: BoField[];
}

export interface CoverageTotals {
  edi_anchors: number;
  bo4e_targets: number;
  entries: number;
  mapped_edi: number;
  unmapped_edi: number;
  unmapped_bo4e: number;
  needs_review: number;
}

export interface Coverage {
  bundle: string;
  message_type: string;
  totals: CoverageTotals;
  unmapped_edi: Array<{ nr?: string; segment_group?: string | null; segment: string }>;
  unmapped_bo4e: Array<{ bo: string; path: string }>;
}

/** PUT-Body: expected version + die änderbaren Felder (Teilmenge). */
export interface EntryUpdatePayload {
  version: number;
  edi_anchor?: Record<string, unknown>;
  bo4e_anchor?: { bo: string; path: string };
  transform?: Transform | Record<string, unknown>;
  direction?: string;
  notes?: string;
  status?: string;
}

export interface UpdateResult {
  bundle: string;
  message_type: string;
  entry: MappingEntry;
  git_commit: string | null;
}

/** POST-Body für ein neues Entry (Server vergibt mapping_id + version). */
export interface EntryCreatePayload {
  edi_anchor: Record<string, unknown>;
  bo4e_anchor: { bo: string; path: string };
  transform: Transform | Record<string, unknown>;
  direction: string;
  notes?: string;
  status?: string;
}

/** Ergebnis von POST /apply (EDI → BO4E inbound). */
export interface ApplyResult {
  bundle: string;
  message_type: string;
  error?: string;
  message?: { type: string; version: string; reference?: string; segments: number };
  bo4e: Record<string, unknown>;
  applied: Array<{
    mapping_id: string;
    edi: { segment: string; qualifier?: string | null; data_element?: string | null; value: unknown };
    bo4e: { bo: string; path: string; value: unknown };
  }>;
  skipped: Array<{ mapping_id: string; reason: string }>;
  stats: { entries_total?: number; applied: number; skipped: number };
  validation?: Record<
    string,
    { validated: boolean; schema: string | null; errors?: Array<{ path: string; message: string }>; note?: string }
  >;
}

/** Antwort von POST /llm/suggest-entry (Teilprojekt B — KI-Assistenz im Popup). */
export interface SuggestResult {
  suggestions: {
    edi_anchor?: Record<string, unknown>;
    bo4e_anchor?: { bo: string; path: string };
    transform?: Record<string, unknown>;
    direction?: string;
  };
  rationale: string;
  confidence?: "low" | "medium" | "high";
}

/** Ein einzelnes Review-Finding (Teilprojekt C — KI-Review). */
export interface ReviewFinding {
  kind: "missing" | "mismatch" | "ok";
  edi_anchor?: Record<string, unknown>;
  bo4e_anchor?: { bo: string; path: string };
  suggested_transform?: Record<string, unknown>;
  suggested_direction?: string;
  severity: "info" | "warn" | "error";
  note: string;
}

/** Antwort von POST /llm/review. */
export interface ReviewResult {
  summary: string;
  findings: ReviewFinding[];
  proposed_drafts: Array<Record<string, unknown>>;
  stats: { items: number; batches: number; missing: number; mismatch: number };
  truncated: boolean;
}

/** 409 vom Server: Entry wurde zwischenzeitlich geändert. */
export class VersionConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "VersionConflictError";
  }
}

async function getJson<T>(url: string): Promise<T> {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
  return r.json() as Promise<T>;
}

export const mapperApi = {
  async getBundles(): Promise<MapperBundle[]> {
    const b = await getJson<{ bundles: MapperBundle[] }>("/api/mapper/bundles");
    return b.bundles;
  },
  getMessageTypes: (bundleId: string) =>
    getJson<{ message_types: string[] }>(
      `/api/mapper/message-types?bundle=${encodeURIComponent(bundleId)}`,
    ),
  getMappings: (bundleId: string, msgtype: string) =>
    getJson<{ entries: MappingEntry[] }>(
      `/api/mapper/mappings/${encodeURIComponent(msgtype)}?bundle=${encodeURIComponent(bundleId)}`,
    ),
  getSkeleton: (bundleId: string, msgtype: string) =>
    getJson<Skeleton>(
      `/api/mapper/skeleton/${encodeURIComponent(msgtype)}?bundle=${encodeURIComponent(bundleId)}`,
    ),
  getBusinessObjects: (bundleId: string) =>
    getJson<{ bundle: string; business_objects: string[] }>(
      `/api/mapper/business-objects?bundle=${encodeURIComponent(bundleId)}`,
    ),
  getBoView: (bundleId: string, bo: string, messageType?: string) =>
    getJson<BoView>(
      `/api/mapper/bo-view?bundle=${encodeURIComponent(bundleId)}&bo=${encodeURIComponent(bo)}` +
        (messageType ? `&message_type=${encodeURIComponent(messageType)}` : ""),
    ),
  getCoverage: (bundleId: string, msgtype: string) =>
    getJson<Coverage>(
      `/api/mapper/coverage/${encodeURIComponent(msgtype)}?bundle=${encodeURIComponent(bundleId)}`,
    ),
  async updateEntry(
    msgtype: string,
    mappingId: string,
    payload: EntryUpdatePayload,
    bundleId: string,
  ): Promise<UpdateResult> {
    const r = await fetch(
      `/api/mapper/mappings/${encodeURIComponent(msgtype)}/${encodeURIComponent(mappingId)}?bundle=${encodeURIComponent(bundleId)}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
    );
    if (r.status === 409) {
      throw new VersionConflictError(
        "Entry wurde zwischenzeitlich geändert — bitte neu laden.",
      );
    }
    if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
    return r.json() as Promise<UpdateResult>;
  },
  async deleteEntry(
    msgtype: string,
    mappingId: string,
    bundleId: string,
    version?: number,
  ): Promise<{ deleted: string; git_commit: string | null }> {
    const v = version != null ? `&version=${version}` : "";
    const r = await fetch(
      `/api/mapper/mappings/${encodeURIComponent(msgtype)}/${encodeURIComponent(mappingId)}?bundle=${encodeURIComponent(bundleId)}${v}`,
      { method: "DELETE" },
    );
    if (r.status === 409)
      throw new VersionConflictError("Entry wurde zwischenzeitlich geändert — bitte neu laden.");
    if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
    return r.json() as Promise<{ deleted: string; git_commit: string | null }>;
  },
  async createEntry(
    msgtype: string,
    payload: EntryCreatePayload,
    bundleId: string,
  ): Promise<UpdateResult> {
    const r = await fetch(
      `/api/mapper/mappings/${encodeURIComponent(msgtype)}?bundle=${encodeURIComponent(bundleId)}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
    );
    if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
    return r.json() as Promise<UpdateResult>;
  },
  async apply(
    edifact: string,
    messageType: string,
    opts: { bundle?: string; includeNeedsReview?: boolean } = {},
  ): Promise<ApplyResult> {
    const r = await fetch("/api/mapper/apply", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        edifact,
        message_type: messageType,
        bundle: opts.bundle ?? null,
        include_needs_review: opts.includeNeedsReview ?? false,
      }),
    });
    if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
    return r.json() as Promise<ApplyResult>;
  },
  getBo4eField: (bo: string, path: string) =>
    getJson<Bo4eFieldInfo>(
      `/api/mapper/bo4e-field?bo=${encodeURIComponent(bo)}&path=${encodeURIComponent(path)}`,
    ),
  getBo4eSchemas: () =>
    getJson<{ count: number; schemas: Record<string, Record<string, unknown>> }>(
      "/api/mapper/bo4e-schema",
    ),
  /** KI-Assistenz (B): schlägt offene Wizard-Teile (Quelle/Ziel/Transform) vor. */
  async suggestEntry(payload: Record<string, unknown>): Promise<SuggestResult> {
    const r = await fetch("/api/mapper/llm/suggest-entry", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
    return r.json() as Promise<SuggestResult>;
  },
  /** KI-Review (C): prüft bzw. ergänzt das Mapping eines BO/Nachrichtentyps. */
  async reviewMapping(payload: {
    scope: "message_type" | "bo";
    target: string;
    bundle?: string;
    mode: "check" | "fill_gaps";
  }): Promise<ReviewResult> {
    const r = await fetch("/api/mapper/llm/review", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
    return r.json() as Promise<ReviewResult>;
  },
};
